import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { FiDownload, FiFile, FiFileText } from 'react-icons/fi';
import { generateBrandVoxReport, fetchBrandCompanyName } from '../utils/pdfUtils';
import { useGlobal } from '../Context/GlobalContext';
import { useWorkspace } from '../Context/WorkspaceContext';
import { useLanguage } from '../Context/LanguageContext';
import { useAIModels } from '../Context/AIModelsContext';
import OutlinedPrimaryButton from './Buttons/OutlinedPrimaryButton';

/**
 * Helper function to normalize text for better compatibility
 * Converts special characters (Turkish, accented, etc.) to ASCII equivalents
 */
const normalizeText = (text) => {
  if (!text) return '';
  const str = String(text);

  const charMap = {
    // Turkish characters
    'ğ': 'g', 'Ğ': 'G',
    'ü': 'u', 'Ü': 'U',
    'ş': 's', 'Ş': 'S',
    'ı': 'i', 'İ': 'I',
    'ö': 'o', 'Ö': 'O',
    'ç': 'c', 'Ç': 'C',
    // Common special characters
    'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a',
    'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e',
    'ì': 'i', 'í': 'i', 'î': 'i', 'ï': 'i',
    'ò': 'o', 'ó': 'o', 'ô': 'o', 'õ': 'o',
    'ù': 'u', 'ú': 'u', 'û': 'u',
    'ñ': 'n', 'ý': 'y', 'ÿ': 'y',
    'À': 'A', 'Á': 'A', 'Â': 'A', 'Ã': 'A', 'Ä': 'A',
    'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E',
    'Ì': 'I', 'Í': 'I', 'Î': 'I', 'Ï': 'I',
    'Ò': 'O', 'Ó': 'O', 'Ô': 'O', 'Õ': 'O',
    'Ù': 'U', 'Ú': 'U', 'Û': 'U',
    'Ñ': 'N', 'Ý': 'Y'
  };

  return str.replace(/[ğĞüÜşŞıİöÖçÇàáâãäèéêëìíîïòóôõùúûñýÿÀÁÂÃÄÈÉÊËÌÍÎÏÒÓÔÕÙÚÛÑÝ]/g,
    char => charMap[char] || char);
};

const ExportDropdown = ({ data, filename, appliedFilters, pagination, isLoading = false, className = "", agentName = null, leadType = null, sessionMetadata = null }) => {
  const { t } = useLanguage();
  const { getModelDisplayName } = useAIModels();
  const [isExporting, setIsExporting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);
  const { apiUrl } = useGlobal();
  const { currentWorkspace } = useWorkspace();

  // Handle dropdown toggle and positioning
  const toggleDropdown = () => {
    if (!showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 180; // minWidth from the dropdown style
      setDropdownPosition({
        x: rect.right - dropdownWidth, // Position so dropdown's right edge aligns with button's right edge
        y: rect.bottom + window.scrollY
      });
    }
    setShowDropdown(!showDropdown);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both button and dropdown menu
      if (buttonRef.current && !buttonRef.current.contains(event.target) &&
        !event.target.closest('[data-dropdown-menu="true"]')) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Export as JSON
  const exportAsJSON = () => {
    setShowDropdown(false);
    try {
      setIsExporting(true);
      const exportData = {
        metadata: {
          exported_at: new Date().toISOString(),
          total_records: pagination?.total_items || data.length,
          export_format: 'json'
        },
        data: data
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
    } finally {
      setIsExporting(false);
    }
  };

  // Export as CSV
  const exportAsCSV = () => {
    setShowDropdown(false);
    try {
      setIsExporting(true);

      if (!data || data.length === 0) {
        alert(t('components.exportdropdown.noDataToExport'));
        return;
      }

      // Detect data type - use explicit leadType first, then lead_type field, then field detection
      const isChatData = data[0]?.question !== undefined;
      const isAgentLeadsData = data[0]?.timestamp !== undefined && data[0]?.source !== undefined && !isChatData;
      const isAnalyticsData = data[0]?.metric !== undefined && data[0]?.category !== undefined;

      // For lead data, prioritize explicit leadType or lead_type field
      let isCompanyData = false;

      if (leadType) {
        // Use explicit leadType from props (from LeadSearch)
        isCompanyData = leadType === 'company' || leadType === 'companies';
      } else if (data[0]?.lead_type) {
        // Use lead_type field (from LeadListModal with mixed types - detect majority)
        const companyCount = data.filter(d => d.lead_type === 'company').length;
        const peopleCount = data.filter(d => d.lead_type === 'person').length;
        isCompanyData = companyCount > peopleCount;
      } else {
        // Fallback to field detection
        isCompanyData = data[0]?.primary_domain !== undefined || data[0]?.industry !== undefined;
      }

      let headers, csvData;

      if (isChatData) {
        // Chat export fields - new structure with separate rows for Q&A
        headers = ['Date & Time', 'Type', 'Sender/Responder', 'Message', 'Feedback', 'Source', 'Country', 'City', 'IP Address'];

        // Get user name from session metadata
        const userName = normalizeText(sessionMetadata?.lead_info?.fullName || sessionMetadata?.lead_info?.name || 'User');

        // Flatten conversations into individual messages
        const messages = [];
        data.forEach(chat => {
          // Add question as separate message if it exists
          if (chat.question && chat.question.trim() !== '') {
            const dateObj = new Date(chat.timestamp);
            const formattedDateTime = `${dateObj.toLocaleDateString()} ${dateObj.toLocaleTimeString()}`;
            messages.push([
              formattedDateTime,
              'Question',
              userName,
              chat.question,
              '', // No feedback for questions
              chat.message_source || 'Unknown',
              chat.country || 'Unknown',
              chat.city || 'Unknown',
              chat.user_ip || 'N/A'
            ]);
          }

          // Add answer as separate message if it exists
          if (chat.answer && chat.answer.trim() !== '') {
            const dateObj = new Date(chat.timestamp);
            const formattedDateTime = `${dateObj.toLocaleDateString()} ${dateObj.toLocaleTimeString()}`;

            let responder = '';
            if (chat.model === 'human') {
              // Human response - show agent name and email
              const agentName = normalizeText(chat.agent_display_name || 'Human Agent');
              const agentEmail = normalizeText(chat.human_support_email || 'N/A');
              responder = `${agentName} (${agentEmail})`;
            } else {
              // AI response - show model name from context
              responder = getModelDisplayName(chat.model) || chat.model || 'Unknown';
            }

            messages.push([
              formattedDateTime,
              'Answer',
              responder,
              chat.answer,
              chat.liked || 'None',
              chat.message_source || 'Unknown',
              chat.country || 'Unknown',
              chat.city || 'Unknown',
              chat.model === 'human' ? 'Human Agent' : (chat.user_ip || 'N/A')
            ]);
          }
        });

        // Sort messages chronologically
        messages.sort((a, b) => new Date(a[0]) - new Date(b[0]));

        csvData = messages;
      } else if (isAgentLeadsData) {
        // Agent Leads export fields - Date, Name, Email, Phone, Location, Source
        headers = ['Date', 'Name', 'Email', 'Phone', 'Location', 'Source'];
        csvData = data.map(lead => {
          const dateObj = new Date(lead.timestamp);
          const formattedDate = dateObj.toLocaleDateString();

          return [
            formattedDate,
            lead.full_name || 'N/A',
            lead.email || 'N/A',
            lead.phone || 'N/A',
            lead.location ?
              (lead.location.city && lead.location.country
                ? `${lead.location.city}, ${lead.location.country}`
                : lead.location.country || lead.location.city || 'N/A'
              ) : 'N/A',
            lead.source || 'N/A'
          ];
        });
      } else if (isCompanyData) {
        // Company export fields - Important columns only, Website instead of Phone
        headers = ['Company Name', 'Domain', 'Industry', 'Employees', 'Location', 'Website URL'];
        csvData = data.map(lead => [
          normalizeText(lead.name) || 'N/A',
          normalizeText(lead.primary_domain) || 'N/A',
          normalizeText(lead.industry) || 'N/A',
          lead.estimated_num_employees ? Math.round(lead.estimated_num_employees).toLocaleString() : 'N/A',
          lead.location ?
            (lead.location.city && lead.location.country
              ? normalizeText(`${lead.location.city}, ${lead.location.country}`)
              : normalizeText(lead.location.country || lead.location.city) || 'N/A'
            ) : (lead.city && lead.country ? normalizeText(`${lead.city}, ${lead.country}`) : normalizeText(lead.country || lead.city) || 'N/A'),
          normalizeText(lead.website_url) || 'N/A'
        ]);
      } else if (isAnalyticsData) {
        // Analytics export fields - normalized structure
        headers = ['Metric', 'Value', 'Date', 'Country', 'Source', 'Model', 'Category'];
        csvData = data.map(item => [
          item.metric || 'N/A',
          item.value || 'N/A',
          item.date || 'N/A',
          item.country || 'N/A',
          item.source || 'N/A',
          item.model || 'N/A',
          item.category || 'N/A'
        ]);
      } else {
        // People export fields - Important columns only, no LinkedIn
        headers = ['Name', 'Email', 'Phone', 'Title', 'Company', 'Seniority', 'Location'];
        csvData = data.map(lead => [
          normalizeText(lead.full_name || lead.name) || 'N/A',
          normalizeText(lead.email) || 'N/A',
          normalizeText(lead.organization?.primary_phone?.number || lead.organization?.phone || lead.phone || lead.primary_phone) || 'N/A',
          normalizeText(lead.title) || 'N/A',
          normalizeText(lead.organization?.name || lead.company) || 'N/A',
          normalizeText(lead.seniority) || 'N/A',
          lead.location ?
            (lead.location.city && lead.location.country
              ? normalizeText(`${lead.location.city}, ${lead.location.country}`)
              : normalizeText(lead.location.country || lead.location.city) || 'N/A'
            ) : (lead.city && lead.country ? normalizeText(`${lead.city}, ${lead.country}`) : normalizeText(lead.country || lead.city) || 'N/A')
        ]);
      }

      // Combine headers and data
      const csvContent = [headers, ...csvData]
        .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
    } finally {
      setIsExporting(false);
    }
  };

  // Export as PDF using unified BrandVox styling
  const exportAsPDF = async () => {
    setShowDropdown(false);
    try {
      setIsExporting(true);

      // Detect data type - use explicit leadType first, then lead_type field, then field detection
      const isChatData = data[0]?.question !== undefined;
      const isAgentLeadsData = data[0]?.timestamp !== undefined && data[0]?.source !== undefined && !isChatData;
      const isAnalyticsData = data[0]?.metric !== undefined && data[0]?.category !== undefined;

      // For lead data, prioritize explicit leadType or lead_type field
      let isCompanyData = false;
      let isPeopleData = false;

      if (leadType) {
        // Use explicit leadType from props (from LeadSearch)
        isCompanyData = leadType === 'company' || leadType === 'companies';
        isPeopleData = leadType === 'people' || leadType === 'person';
      } else if (data[0]?.lead_type) {
        // Use lead_type field (from LeadListModal with mixed types - detect majority)
        const companyCount = data.filter(d => d.lead_type === 'company').length;
        const peopleCount = data.filter(d => d.lead_type === 'person').length;
        isCompanyData = companyCount > peopleCount;
        isPeopleData = peopleCount >= companyCount;
      } else {
        // Fallback to field detection
        isCompanyData = data[0]?.primary_domain !== undefined || data[0]?.industry !== undefined;
        isPeopleData = data[0]?.full_name !== undefined || data[0]?.email !== undefined;
      }

      const reportType = isAnalyticsData ? 'Analytics' :
        isAgentLeadsData ? 'Leads' :
          isPeopleData ? 'People Leads' :
            isCompanyData ? 'Company Leads' :
              isChatData ? 'Chats' : 'Data';

      // Build summary data based on whether it's chat data or not
      const summaryData = [];

      summaryData.push(['Report Type', reportType]);
      summaryData.push(['Total Records', (pagination?.total_items || data.length).toString()]);

      // Add Support BV if available (for chat and agent leads exports)
      if (agentName) {
        summaryData.push(['Support BV', agentName]);
      }

      // Add lead info for chat data
      if (isChatData && sessionMetadata?.lead_info) {
        const leadInfo = sessionMetadata.lead_info;
        if (leadInfo.fullName) {
          summaryData.push(['User Name', leadInfo.fullName]);
        }
        if (leadInfo.email) {
          summaryData.push(['User Email', leadInfo.email]);
        }
        if (leadInfo.phone) {
          summaryData.push(['User Phone', leadInfo.phone]);
        }
      }

      // For chat data, show liked/disliked counts instead of date range
      if (isChatData) {
        const likedCount = data.filter(chat => chat.liked === 'Liked').length;
        const dislikedCount = data.filter(chat => chat.liked === 'Disliked').length;
        summaryData.push(['Liked/Disliked', `${likedCount} Liked / ${dislikedCount} Disliked`]);
      } else if (appliedFilters?.start_date && appliedFilters?.end_date &&
        appliedFilters.start_date !== null && appliedFilters.end_date !== null) {
        // Show date range if available and not null
        summaryData.push(['Date Range', `${appliedFilters.start_date} to ${appliedFilters.end_date}`]);
      } else {
        // Fallback to generated timestamp if no date range
        summaryData.push(['Generated', `${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`]);
      }

      let tableData, headers, columnStyles, conversations;

      if (isAgentLeadsData) {
        // Agent Leads data - Date, Name, Email, Phone, Location, Source
        headers = ['Date', 'Name', 'Email', 'Phone', 'Location', 'Source'];
        tableData = data.map(item => {
          const dateObj = new Date(item.timestamp);
          const formattedDate = dateObj.toLocaleDateString();

          return [
            formattedDate,
            item.full_name || 'N/A',
            item.email || 'N/A',
            item.phone || 'N/A',
            item.location ?
              (item.location.city && item.location.country
                ? `${item.location.city}, ${item.location.country}`
                : item.location.country || item.location.city || 'N/A'
              ) : 'N/A',
            item.source || 'N/A'
          ];
        });
        columnStyles = {
          0: { cellWidth: 35 },  // Date
          1: { cellWidth: 35 },  // Name
          2: { cellWidth: 45 },  // Email
          3: { cellWidth: 35 },  // Phone
          4: { cellWidth: 40 },  // Location
          5: { cellWidth: 30 }   // Source
        };
      } else if (isCompanyData) {
        // Company leads data - Important columns only, Website instead of Phone
        headers = ['Company', 'Domain', 'Industry', 'Employees', 'Location', 'Website'];
        tableData = data.map(item => [
          normalizeText(item.name) || 'N/A',
          normalizeText(item.primary_domain) || 'N/A',
          normalizeText(item.industry) || 'N/A',
          item.estimated_num_employees ? Math.round(item.estimated_num_employees).toLocaleString() : 'N/A',
          item.location ?
            (item.location.city && item.location.country
              ? normalizeText(`${item.location.city}, ${item.location.country}`)
              : normalizeText(item.location.country || item.location.city) || 'N/A'
            ) : (item.city && item.country ? normalizeText(`${item.city}, ${item.country}`) : normalizeText(item.country || item.city) || 'N/A'),
          normalizeText(item.website_url) || 'N/A'
        ]);
        columnStyles = {
          0: { cellWidth: 40 },
          1: { cellWidth: 40 },
          2: { cellWidth: 35 },
          3: { cellWidth: 30 },
          4: { cellWidth: 40 },
          5: { cellWidth: 35 }
        };
      } else if (isPeopleData) {
        // People leads data - Important columns only, no LinkedIn
        headers = ['Name', 'Email', 'Phone', 'Title', 'Company', 'Seniority', 'Location'];
        tableData = data.map(item => [
          normalizeText(item.full_name || item.name) || 'N/A',
          normalizeText(item.email) || 'N/A',
          normalizeText(item.organization?.primary_phone?.number || item.organization?.phone || item.phone || item.primary_phone) || 'N/A',
          normalizeText(item.title) || 'N/A',
          normalizeText(item.organization?.name || item.company) || 'N/A',
          normalizeText(item.seniority) || 'N/A',
          item.location ?
            (item.location.city && item.location.country
              ? normalizeText(`${item.location.city}, ${item.location.country}`)
              : normalizeText(item.location.country || item.location.city) || 'N/A'
            ) : (item.city && item.country ? normalizeText(`${item.city}, ${item.country}`) : normalizeText(item.country || item.city) || 'N/A')
        ]);
        columnStyles = {
          0: { cellWidth: 30 },
          1: { cellWidth: 40 },
          2: { cellWidth: 30 },
          3: { cellWidth: 30 },
          4: { cellWidth: 35 },
          5: { cellWidth: 25 },
          6: { cellWidth: 35 }
        };
      } else if (isChatData) {
        // Chat data - pass as conversations to use special conversation table
        conversations = data; // Pass the raw data with all fields
        headers = null;
        tableData = null;
        columnStyles = null;
      } else if (isAnalyticsData) {
        // Analytics data - normalize the structure
        headers = ['Metric', 'Value', 'Date', 'Country', 'Source', 'Model', 'Category'];
        tableData = data.map(item => [
          item.metric || 'N/A',
          item.value || 'N/A',
          item.date || 'N/A',
          item.country || 'N/A',
          item.source || 'N/A',
          item.model || 'N/A',
          item.category || 'N/A'
        ]);
        columnStyles = {
          0: { cellWidth: 40 },  // Metric
          1: { cellWidth: 25 },  // Value
          2: { cellWidth: 30 },  // Date
          3: { cellWidth: 30 },  // Country
          4: { cellWidth: 30 },  // Source
          5: { cellWidth: 30 },  // Model
          6: { cellWidth: 35 }   // Category
        };
      } else {
        // Generic data handling
        headers = data.length > 0 ? Object.keys(data[0]) : [];
        tableData = data.map(item => Object.values(item).map(val => String(val || 'N/A')));
        columnStyles = {};
      }

      // Fetch company name for PDF header
      const companyName = await fetchBrandCompanyName(apiUrl, currentWorkspace?.workspace_id);

      // Generate PDF using unified styling (now async)
      await generateBrandVoxReport({
        title: `BrandVox AI ${reportType} Report`,
        subtitle: `Generated on ${new Date().toLocaleDateString()}`,
        summaryData: summaryData,
        dataTitle: `${reportType} Data`,
        headers: headers,
        data: tableData,
        conversations: conversations, // Pass conversations for chat data
        columnStyles: columnStyles,
        filename: `brandvox-${filename.toLowerCase()}`,
        appliedFilters: appliedFilters,
        companyName: companyName,
        agentName: agentName,
        sessionId: appliedFilters?.session_id,
        sessionMetadata: sessionMetadata, // Pass session metadata for lead info
        getModelDisplayName: getModelDisplayName // Pass dynamic model name resolver from context
      });

    } catch (error) {
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <OutlinedPrimaryButton
        ref={buttonRef}
        disabled={isLoading || isExporting || !data || data.length === 0}
        onClick={toggleDropdown}
        width='7rem'
        mobileWidth='7rem'
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {isExporting ? (
          <>
            <Spinner animation="border" size="sm" className="me-1" />
            {t('components.exportdropdown.exporting')}
          </>
        ) : (
          <>
            <FiDownload className="me-1" />
            {t('components.exportdropdown.export')}
          </>
        )}
      </OutlinedPrimaryButton>

      {showDropdown && createPortal(
        <div
          data-dropdown-menu="true"
          style={{
            position: 'fixed',
            left: dropdownPosition.x,
            top: dropdownPosition.y,
            zIndex: 10000,
            background: 'white',
            border: '1px solid rgba(0, 0, 0, 0.15)',
            borderRadius: '0.375rem',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
            minWidth: '180px',
            padding: '0.5rem 0',
          }}
        >
          <div
            style={{
              padding: '0.375rem 1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.875rem',
              color: '#212529',
              textDecoration: 'none',
              backgroundColor: 'transparent',
              border: 'none',
              width: '100%',
              textAlign: 'left'
            }}
            onClick={exportAsJSON}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FiFile className="me-2" />
            {t('components.exportdropdown.exportAsJSON')}
          </div>
          <div
            style={{
              padding: '0.375rem 1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.875rem',
              color: '#212529',
              textDecoration: 'none',
              backgroundColor: 'transparent',
              border: 'none',
              width: '100%',
              textAlign: 'left'
            }}
            onClick={exportAsCSV}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FiFileText className="me-2" />
            {t('components.exportdropdown.exportAsCSV')}
          </div>
          <div
            style={{
              padding: '0.375rem 1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.875rem',
              color: '#212529',
              textDecoration: 'none',
              backgroundColor: 'transparent',
              border: 'none',
              width: '100%',
              textAlign: 'left'
            }}
            onClick={exportAsPDF}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FiFileText className="me-2" />
            {t('components.exportdropdown.exportAsPDF')}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ExportDropdown;